import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { Placeholder } from "@/components/Placeholder";
import { SERVICES } from "@/data/content";

const FACTORS = [
  { title: "Scope", text: "Number of pages, features, integrations, or the breadth of the operational work." },
  { title: "Complexity", text: "Custom design, third-party systems, data migration or multi-step workflows cost more than standard work." },
  { title: "Content readiness", text: "Text, images and brand assets supplied ready to use reduce effort; content we must write or source adds to it." },
  { title: "Duration", text: "One-off projects are quoted as a set project fee; ongoing services (maintenance, SEO, marketing, operations support) as a monthly retainer." },
];

const CONTAINS = [
  "Service name and description",
  "Price and currency (USD)",
  "What is included and what is not",
  "Delivery timeframe or service period",
  "Payment terms and milestones (50% / 50%)",
  "Revision allowance",
  "Cancellation and refund terms",
  "Links to our Terms, Privacy and Refund policies",
];

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <Seo title="Pricing & Packages" description="How My Geeks LLC prices website development, business management, SEO/SMO and digital marketing. All services are quoted in writing — request a quote." />
      <PageHeader overline="Pricing & packages" title="Quoted in writing, before any work begins" lede="We do not publish a standard price list. Every engagement is priced by written quotation after a short discovery call, so the figure you receive is the figure that applies to your project." />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.06} className="h-full">
              <article data-testid={`pricing-card-${s.slug}`} className="card card-hover flex h-full flex-col p-7">
                <p className="label-xs">Service {s.numeral}</p>
                <h2 className="mt-2 font-display text-xl font-bold tracking-tight">{s.title}</h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{s.short}</p>
                <div className="mt-6 border-t border-slate-200 pt-5">
                  <p data-testid={`pricing-value-${s.slug}`} className="font-display text-2xl font-bold">{s.pricing}</p>
                  <p className="mt-2 text-xs leading-relaxed text-slate-500">{s.pricingNote}</p>
                  <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-cream px-3 py-1.5 text-xs text-slate-700">
                    <Clock size={13} className="text-brand" /> <Placeholder field={s.timeframe} />
                  </p>
                </div>
                <Link to={`/contact?service=${s.slug}`} data-testid={`pricing-quote-${s.slug}`} className="btn-primary mt-6 w-full">
                  Request a Quote <ArrowRight size={14} />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24">
          <Reveal>
            <p className="eyebrow">Why the price varies</p>
            <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">What determines your quotation</h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {FACTORS.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.06}>
                <div data-testid={`pricing-factor-${i + 1}`} className="card p-7">
                  <h3 className="font-display text-lg font-semibold tracking-tight">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 md:py-24">
        <Reveal>
          <div data-testid="pricing-what-you-receive" className="card p-8 md:p-10">
            <p className="eyebrow">What your quotation contains</p>
            <ul className="mt-6 grid gap-3 text-sm leading-relaxed text-slate-700 sm:grid-cols-2">
              {CONTAINS.map((t) => (
                <li key={t} className="flex gap-2.5"><Check size={16} className="mt-0.5 shrink-0 text-forest" />{t}</li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-slate-500">
              Quotations are free, valid for 3 days and carry no obligation. See our{" "}
              <Link to="/payment-information" className="font-medium text-brand hover:underline">Payment Information</Link> page for how invoices are issued and paid.
            </p>
          </div>
        </Reveal>
      </section>

      <CTABand title="Get a written quotation." text="Tell us about the project and we'll reply with a clear price and scope." />
    </div>
  );
}
